import { useEffect, useRef, type RefObject } from 'react'
import { useLocation, useNavigationType } from 'react-router-dom'

/**
 * Lands a NEW route at the top, with focus on the main region (FF-AUDIT-001).
 *
 * The document is the page's only scroller, and a client-side navigation does
 * not touch its scroll position. So a user who scrolled down to reach a lower
 * sidebar entry — which the old unbounded sidebar made necessary — opened the
 * destination partway down, heading and primary actions above the viewport,
 * and read the half-empty screen as missing functionality.
 *
 * Three deliberate exclusions, each a behaviour this must NOT break:
 *
 * - `POP` (browser back/forward): the browser restores the position it saved
 *   for that history entry; overriding it would break back-restoration.
 * - A hash: `useScrollToHash` owns that scroll (the hub-card links).
 * - Search-param-only changes: filters, tabs, sort and page live in the URL
 *   (UIX-1) and change on the same pathname; scrolling a long list to the top
 *   on every filter keystroke would be a regression, so the effect keys on
 *   `pathname` alone.
 *
 * Focus moves to `<main tabIndex={-1}>` so a keyboard or screen-reader user
 * continues from the destination's heading rather than from wherever the
 * clicked sidebar link left them. The initial render is skipped: the browser
 * has already placed the document and stealing focus on load is an a11y
 * defect of its own.
 */
export function useRouteScrollReset(main: RefObject<HTMLElement | null>): void {
  const { pathname, hash } = useLocation()
  const navigationType = useNavigationType()
  const first = useRef(true)

  useEffect(() => {
    if (first.current) {
      first.current = false
      return
    }
    if (navigationType === 'POP' || hash) return
    window.scrollTo({ top: 0, left: 0 })
    main.current?.focus({ preventScroll: true })
    // Keyed on the pathname only — see the docblock for why `search` is out.
    // `hash` and `navigationType` describe THIS navigation and are read with
    // it; listing them would reset scroll and steal focus on a hash-only or
    // same-path change. `main` is a ref object, stable for the caller's life.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fires on a path change only, by design
  }, [pathname])
}
